import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["withPadding", "children"];
// React and Redux
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

// External libraries
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { List, Range } from 'immutable';
import styled, { useTheme } from 'foundations-theming/utils';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingDots } from 'foundations-components/transitional/components/LoadingDots';
import { OverlayContent } from 'foundations-components/transitional/components/Overlay';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { View } from 'foundations-components/components/View';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import { Text } from 'foundations-components/components/Text';
import { getEditLinkURL as getEditLinkURLSelector, getUserTimezone, getEvent as getEventSelector, getHasCustomAvailability, getHasMoreLinkAvailability, getHasMoreLinkAvailabilityByMonthOffset, getIsProcessingLinkAvailability, getCustomAvailabilityStartDate, getPushbackTime } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getAvailableDates as getAvailableDatesSelector, isSelectedDateAvailable as getIsSelectedDateAvailable } from 'MeetingsLib/selectors/AvailabilitySelectors';
import { getIsFetchingPublicAvailability } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { getCurrentTime } from 'MeetingsLib/selectors/RESTParamSelectors';
import { getConfiguredAvailabilityByDate, getTroubleshootTimesForSelectedDurationByDay, getConfiguredBookableDates, getMinimumNoticeTimeBusyDays, isTroubleshootingSelector } from 'MeetingsLib/selectors/TroubleshootingSelectors';
// MeetingsLib components
import DatePickerButton from 'MeetingsLib/components/schedule/date/DatePickerButton';
import DatePickerHeader from './DatePickerHeaderV2';
import { HiddenAriaStatus } from 'MeetingsLib/components/decorative/HiddenAriaStatus';
import ZeroState from 'MeetingsLib/components/schedule/ZeroState';
// Contexts for PublicAvailabilityInfo and TroubleshootTimes
import { useIsLoadingLinkAvailabilityPage, useMonthAvailabilityPeek, useSetMonthOffset } from 'MeetingsLib/context/PublicAvailabilityInfoContext';
import { useIsLoadingTroubleshootPage, useSetTroubleshootMonthOffset } from 'MeetingsLib/context/TroubleshootTimesContext';
// MeetingsBase Models and Utilities

import { getPrimaryColorOrDefault, getTextColor } from 'MeetingsBase/utils/color';
// Types

import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTable = styled.table.withConfig({
  displayName: "DatePickerV2__StyledTable"
})(["table-layout:fixed;width:100%;margin:0;"]);
const CalendarOverlay = _ref => {
  let {
      withPadding,
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(TFlex, Object.assign({
    position: "absolute",
    inlineSize: "100%",
    blockSize: "100%",
    alignItems: "center",
    justifyContent: "center",
    direction: "column",
    zIndex: "2",
    paddingInline: withPadding ? '200' : '0',
    marginInline: "auto",
    inset: "0"
  }, rest, {
    children: children
  }));
};
const NUM_DAYS = 7;
const NUM_WEEKS = 5;
const DAY_RANGE = Range().take(NUM_DAYS).toArray();
const AUTO_ADVANCE_LOOKAHEAD_MONTHS = 1;
function isDateInMonth(momentDate, momentMonth) {
  return momentMonth.month() === momentDate.month();
}
function getWeekRange() {
  return Range().take(NUM_WEEKS + 1).toArray();
}
function getDateStringsBetween(startDate, endDate) {
  const currentDate = startDate.clone();
  const dates = [];
  while (currentDate.isSameOrBefore(endDate)) {
    dates.push(currentDate.format('YYYY-MM-DD'));
    currentDate.add(1, 'day');
  }
  return dates;
}
const NoAvailableDates = ({
  getNextMonth,
  hasMoreAvailability,
  isTroubleshooting,
  startOfMonth,
  endOfMonth,
  availableDateOptions,
  isProcessingLinkAvailability,
  renderedMonthLabelRef,
  brandingTextStyle
}) => {
  if (isProcessingLinkAvailability) {
    return null;
  }
  const datesWithinMonth = getDateStringsBetween(startOfMonth, endOfMonth);
  const shouldRenderNextMonthCTA = !datesWithinMonth.some(dateString => availableDateOptions.includes(dateString));
  const noDatesAvailable = availableDateOptions.length === 0 && !hasMoreAvailability;
  if (!shouldRenderNextMonthCTA || isTroubleshooting) {
    return null;
  }
  if (
  // eslint-disable-next-line react-hooks/refs
  renderedMonthLabelRef.current &&
  // eslint-disable-next-line react-hooks/refs
  renderedMonthLabelRef.current.innerText !== startOfMonth.format('MMMM YYYY')) {
    return null;
  }
  return /*#__PURE__*/_jsx(CalendarOverlay, {
    "data-test-id": "no-dates-available-container",
    marginleft: noDatesAvailable ? '85px' : undefined,
    withPadding: true,
    children: /*#__PURE__*/_jsx(OverlayContent, {
      children: /*#__PURE__*/_jsxs(TFlex, {
        direction: "column",
        gap: 1,
        alignItems: "center",
        children: [/*#__PURE__*/_jsx("b", {
          style: brandingTextStyle,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "prebook.datePicker.noPreviousMonthReason",
            options: {
              month: startOfMonth.format('MMMM')
            }
          })
        }), !noDatesAvailable && /*#__PURE__*/_jsxs(Button, {
          use: "transparent",
          onClick: getNextMonth,
          "data-test-id": "getNextMonth-btn",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "prebook.datePicker.nextMonthCTA"
          }), /*#__PURE__*/_jsx(RightIcon, {
            size: "100"
          })]
        })]
      })
    })
  }, startOfMonth.format('MMMM'));
};
function DatePicker({
  branding,
  compact,
  profile,
  setEventDate,
  DatePickerMarker,
  TimePickerMarker,
  isPreviewed
}) {
  const theme = useTheme();
  const bookableDates = useSelector(getConfiguredBookableDates);
  const configuredAvailabilityByDate = useSelector(getConfiguredAvailabilityByDate);
  const hasCustomAvailability = useSelector(getHasCustomAvailability);
  const hasMoreAvailability = useSelector(getHasMoreLinkAvailability);
  const hasMoreByMonthOffset = useSelector(getHasMoreLinkAvailabilityByMonthOffset);
  const customAvailabilityStartDate = useSelector(getCustomAvailabilityStartDate);
  const currentTime = useSelector(getCurrentTime);
  const availableDateOptions = useSelector(getAvailableDatesSelector);
  const isSelectedDateAvailable = useSelector(getIsSelectedDateAvailable);
  const editLinkURL = useSelector(getEditLinkURLSelector);
  const troubleshootTimesForSelectedDurationByDay = useSelector(getTroubleshootTimesForSelectedDurationByDay);
  const isFetchingPublicAvailability = useSelector(getIsFetchingPublicAvailability);
  const minimumNoticeTimeBusyDays = useSelector(getMinimumNoticeTimeBusyDays);
  const pushbackTime = useSelector(getPushbackTime);
  const isTroubleshooting = useSelector(isTroubleshootingSelector);
  const userTimezone = useSelector(getUserTimezone);
  const selectedDate = useSelector( /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */
  state => getEventSelector(state).dateString);
  const setMonthOffset = useSetMonthOffset();
  const setTroubleshootMonthOffset = useSetTroubleshootMonthOffset();
  // determines when linkAvailability processing is complete
  const isProcessingLinkAvailability = useSelector(getIsProcessingLinkAvailability);
  const isLoadingLinkAvailabilityPage = useIsLoadingLinkAvailabilityPage();
  const isLoadingTroubleshootPage = useIsLoadingTroubleshootPage();
  const isLoadingPage = isLoadingLinkAvailabilityPage || isLoadingTroubleshootPage;
  const [monthsAhead, setMonthsAhead] = useState(0);
  const renderedMonthLabelRef = useRef(null);
  const [hasDecidedAutoAdvance, setHasDecidedAutoAdvance] = useState(false);

  /**
   * Base month needs to be the current date because
   * it is possible for no availability to be available from now into future months
   * We do not want a future month i.e availableDateOptions.min() to be the base month
   * unless custom availability has been set
   */
  const baseMonth = useMemo(() => {
    if (hasCustomAvailability && !isTroubleshooting) {
      // Ensure that custom availability is not in the past
      const baseDateForCustomAvailability = Math.max(customAvailabilityStartDate !== null && customAvailabilityStartDate !== void 0 ? customAvailabilityStartDate : 0,
      // eslint-disable-next-line react-hooks/purity
      Date.now() + pushbackTime);
      // custom availability start date is persisted to the BE in UTC
      // If we do not parse in UTC we could end up being a day behind and essentially a month behind
      return I18n.moment(baseDateForCustomAvailability).utc();
    }
    return I18n.moment();
  }, [isTroubleshooting, hasCustomAvailability, customAvailabilityStartDate, pushbackTime]);
  const startOfMonth = useMemo(() => {
    return I18n.moment(baseMonth).startOf('month').add(monthsAhead, 'months');
  }, [baseMonth, monthsAhead]);
  const endOfMonth = useMemo(() => {
    return I18n.moment(baseMonth).add(monthsAhead, 'months').endOf('month');
  }, [baseMonth, monthsAhead]);
  const availableDateOptionsForCurrentMonth = useMemo(() => {
    const startOfMonthLocaleString = startOfMonth.format('YYYY-MM');
    const availableDateArray = availableDateOptions.toArray();
    const availableDatesForStartOfMonth = availableDateArray.filter(dateString => dateString.includes(startOfMonthLocaleString));
    return availableDatesForStartOfMonth;
  }, [startOfMonth, availableDateOptions]);
  const noAvailabilityInMobileView = useMemo(() => {
    return compact && !availableDateOptions.count() && !isFetchingPublicAvailability && !hasMoreAvailability;
  }, [availableDateOptions, compact, hasMoreAvailability, isFetchingPublicAvailability]);
  const localeData =
  // @ts-ignore locale not typed
  I18n.moment.localeData(I18n.locale) || I18n.moment.localeData();
  // some locales start their week on Monday
  // @ts-ignore _week may not be typed
  const daysToShiftForward = localeData._week.dow;
  const getDaysToShiftBack = useCallback(dateObject => {
    const daysFromSunday = dateObject.isoWeekday() % 7;

    /*
     * if the month starts on Sunday and we are in a locale
     * that starts weeks on Monday, we would cut off the first
     * Sunday unless we rendered the entire previous week
     */
    if (daysFromSunday === 0 && daysToShiftForward === 1) {
      return 6;
    }
    return daysFromSunday - daysToShiftForward;
  }, [daysToShiftForward]);
  const getStartOfWeek = useCallback(dateObject => {
    const daysToShiftBack = getDaysToShiftBack(dateObject);
    return I18n.moment(dateObject).subtract(daysToShiftBack, 'days');
  }, [getDaysToShiftBack]);
  const getNextMonth = useCallback(() => {
    setMonthsAhead(_monthsAhead => _monthsAhead + 1);
  }, [setMonthsAhead]);
  const startOfWeek = useMemo(() => getStartOfWeek(startOfMonth), [getStartOfWeek, startOfMonth]);
  useEffect(() => {
    if (!isPreviewed) {
      setMonthOffset(monthsAhead);
    }
  }, [isPreviewed, monthsAhead, setMonthOffset]);
  useEffect(() => {
    if (isTroubleshooting) {
      setTroubleshootMonthOffset(monthsAhead);
    }
  }, [isTroubleshooting, setTroubleshootMonthOffset, monthsAhead]);
  useEffect(() => {
    if (!compact && !isSelectedDateAvailable) {
      setEventDate(availableDateOptions.min());
    }
  }, [compact, isSelectedDateAvailable, availableDateOptions, setEventDate]);

  /**
   * Auto advance logic: if the current month has no availability and next month
   * is able to be booked then peek at the next month. If the next month has
   * availability, then jump to it. Otherwise, stay at the current month.
   * This runs once per page load.
   */
  const canDecideAutoAdvance = !hasDecidedAutoAdvance && !isTroubleshooting && !isPreviewed && !isLoadingPage && monthsAhead === 0 && Boolean(hasMoreByMonthOffset && hasMoreByMonthOffset.has('0'));
  const shouldPeekNextMonth = canDecideAutoAdvance && availableDateOptionsForCurrentMonth.length === 0 && hasMoreAvailability;
  const nextMonthPeek = useMonthAvailabilityPeek({
    monthOffset: AUTO_ADVANCE_LOOKAHEAD_MONTHS,
    shouldPeek: shouldPeekNextMonth
  });
  const isCalendarLoading = isLoadingPage || shouldPeekNextMonth;
  useEffect(() => {
    if (!canDecideAutoAdvance) {
      return;
    }
    if (shouldPeekNextMonth && !nextMonthPeek.isSettled) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasDecidedAutoAdvance(true);
    if (shouldPeekNextMonth && nextMonthPeek.hasAvailability) {
      setMonthsAhead(AUTO_ADVANCE_LOOKAHEAD_MONTHS);
    }
  }, [canDecideAutoAdvance, shouldPeekNextMonth, nextMonthPeek.isSettled, nextMonthPeek.hasAvailability]);
  const handleSetEventDate = useCallback(date => {
    if (isTroubleshooting || availableDateOptions.includes(date)) {
      setEventDate(date);
    }
  }, [isTroubleshooting, availableDateOptions, setEventDate]);
  const brandingTextStyle = useMemo(() => {
    const {
      primaryColor
    } = branding;
    if (!primaryColor) {
      return null;
    }
    return {
      color: getPrimaryColorOrDefault(primaryColor, theme)
    };
  }, [branding, theme]);
  const abbreviatedWeekdays = useMemo(() => {
    // @ts-ignore untyped yet
    return List(localeData._weekdaysShort);
  }, [localeData]);
  const daysToShift = useMemo(() => {
    return abbreviatedWeekdays.take(daysToShiftForward);
  }, [abbreviatedWeekdays, daysToShiftForward]);
  const weekdayNames = useMemo(() => {
    return abbreviatedWeekdays.splice(0, daysToShiftForward).concat(daysToShift);
  }, [abbreviatedWeekdays, daysToShiftForward, daysToShift]);
  const renderDays = weekNumber => {
    return DAY_RANGE.map(daysToAdd => {
      const date = I18n.moment(startOfWeek).add({
        days: daysToAdd,
        weeks: weekNumber
      }).format('YYYY-MM-DD');
      const isDateInCurrentMonth = isDateInMonth(I18n.moment(startOfWeek).add({
        days: daysToAdd,
        weeks: weekNumber
      }), startOfMonth);
      return /*#__PURE__*/_jsx(DatePickerButton, {
        daysToAdd: daysToAdd,
        branding: branding
        /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */,
        currentTime: currentTime,
        date: date,
        isDateInCurrentMonth: isDateInCurrentMonth,
        availableDates: availableDateOptions,
        handleSetEventDate: handleSetEventDate
        // troubleshoot mode
        /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */,
        bookableDates: bookableDates
        /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */,
        configuredAvailabilityByDate: configuredAvailabilityByDate,
        editLinkURL: editLinkURL
        /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */,
        troubleshootTimesForSelectedDurationByDay: troubleshootTimesForSelectedDurationByDay
        /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */,
        minimumNoticeTimeBusyDays: minimumNoticeTimeBusyDays,
        isTroubleshooting: isTroubleshooting,
        userTimezone: userTimezone,
        isLoadingLinkAvailabilityPage: isCalendarLoading
      }, `${date}-${daysToAdd}`);
    });
  };
  function renderWeeksOrEmpty() {
    if (noAvailabilityInMobileView) {
      // Render empty state for compact view
      return /*#__PURE__*/_jsx(View, {
        textAlign: "center",
        marginBlockStart: "1200",
        marginBlockEnd: "1200",
        children: /*#__PURE__*/_jsx(ZeroState, {
          profile: profile,
          TimePickerMarker: TimePickerMarker
        })
      });
    }
    const currentMonth = startOfMonth.format('MMMM');
    const textColorToken = getTextColor(branding.primaryColor, theme, true);
    const weeks = getWeekRange().map(weeksToAdd => {
      return /*#__PURE__*/_jsx("tr", {
        "data-test-id": "extraWeek",
        children: renderDays(weeksToAdd)
      }, weeksToAdd);
    });
    return /*#__PURE__*/_jsxs(View, {
      children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.datePicker.caption",
          options: {
            currentMonth
          }
        })
      }), /*#__PURE__*/_jsxs(StyledTable, {
        children: [/*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsx("tr", {
            children: weekdayNames.map(dayName => {
              return (
                /*#__PURE__*/
                // @bend-expect trellis-tokenized TableHeaderCell has a background color and we don't want that
                _jsx("th", {
                  scope: "col",
                  children: /*#__PURE__*/_jsx(Text, {
                    as: "h6",
                    color: textColorToken,
                    textTransform: "uppercase",
                    children: dayName
                  })
                }, dayName)
              );
            })
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: weeks
        })]
      })]
    });
  }
  const localizedDate = useMemo(() => {
    return selectedDate ? I18n.moment(selectedDate).format('MMMM Do') : '';
  }, [selectedDate]);
  return /*#__PURE__*/_jsxs(TFlex, {
    direction: "column",
    align: "center",
    "data-test-id": "booking-schedule-date-picker",
    children: [/*#__PURE__*/_jsx(DatePickerHeader, {
      startOfMonth: startOfMonth,
      availableDateOptions: availableDateOptions,
      compact: compact,
      isTroubleshooting: isTroubleshooting,
      setMonthsAhead: setMonthsAhead,
      branding: branding,
      isLoadingLinkAvailabilityPage: isCalendarLoading,
      hasCustomAvailability: hasCustomAvailability,
      renderedMonthLabelRef: renderedMonthLabelRef,
      isPreviewed: isPreviewed
    }), /*#__PURE__*/_jsxs(View, {
      position: "relative",
      children: [renderWeeksOrEmpty(), !isCalendarLoading && !noAvailabilityInMobileView && /*#__PURE__*/_jsx(NoAvailableDates, {
        getNextMonth: getNextMonth,
        hasMoreAvailability: hasMoreAvailability,
        isTroubleshooting: isTroubleshooting,
        startOfMonth: startOfMonth,
        isProcessingLinkAvailability: isProcessingLinkAvailability,
        endOfMonth: endOfMonth,
        availableDateOptions: availableDateOptionsForCurrentMonth,
        renderedMonthLabelRef: renderedMonthLabelRef,
        brandingTextStyle: brandingTextStyle
      }), isCalendarLoading &&
      /*#__PURE__*/
      // @bend-expect trellis-tokenized we cannot override the backgroundColor of the Overlay component
      _jsx(CalendarOverlay, {
        children: /*#__PURE__*/_jsx(LoadingDots, {
          "data-test-id": "loading-availability-dots"
        })
      })]
    }), /*#__PURE__*/_jsx(DatePickerMarker, {}), /*#__PURE__*/_jsx(HiddenAriaStatus, {
      role: "status",
      "aria-live": "polite",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "accessible.calendarStatus",
        options: {
          selectedDate: localizedDate
        }
      })
    })]
  });
}
export default DatePicker;